// pages/hr/wizard/steps/EmploymentTypeStep.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Button,
  Card,
  Radio,
  RadioGroup,
  Divider,
  Alert,
} from '@mui/joy';
import { Briefcase as WorkOutlined, Receipt as ReceiptLongOutlined } from '@phosphor-icons/react';
import type { WizardStepProps, MemberType } from '../../../../types/hr';

interface EmploymentTypeData {
  member_type: MemberType;
  job_title: string;
  department: string;
  start_date: string;
  pay_rate: string;
  pay_frequency: 'hourly' | 'salary' | 'per_job';
  has_direct_deposit: boolean;
  bank_name: string;
  routing_number: string;
  account_number: string;
  account_type: 'checking' | 'savings';
}

export const EmploymentTypeStep: React.FC<WizardStepProps> = ({
  step,
  sessionState,
  onDataChange,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState<EmploymentTypeData>({
    member_type: sessionState.member_type,
    job_title: '',
    department: '',
    start_date: '',
    pay_rate: '',
    pay_frequency: 'hourly',
    has_direct_deposit: false,
    bank_name: '',
    routing_number: '',
    account_number: '',
    account_type: 'checking',
    ...((sessionState.step_data[step.id] || {}) as Partial<EmploymentTypeData>),
  });

  const [errors, setErrors] = useState<Partial<Record<keyof EmploymentTypeData, string>>>({});

  useEffect(() => {
    onDataChange(formData as unknown as Record<string, unknown>);
  }, [formData]);

  const handleChange = (field: keyof EmploymentTypeData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof EmploymentTypeData, string>> = {};

    if (!formData.job_title.trim()) newErrors.job_title = 'Job title is required';
    if (!formData.start_date) newErrors.start_date = 'Start date is required';

    if (formData.has_direct_deposit) {
      if (!formData.bank_name.trim()) newErrors.bank_name = 'Bank name is required';
      if (!formData.routing_number.trim()) newErrors.routing_number = 'Routing number is required';
      if (!formData.account_number.trim()) newErrors.account_number = 'Account number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h4" sx={{ mb: 1 }}>
        Employment Details
      </Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
        Confirm your employment type and provide additional details.
      </Typography>

      {/* Employment Type Display */}
      <Typography level="title-md" sx={{ mb: 2 }}>
        Employment Classification
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card
          variant={formData.member_type === 'w2' ? 'soft' : 'outlined'}
          color={formData.member_type === 'w2' ? 'primary' : 'neutral'}
          sx={{ flex: 1, p: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WorkOutlined />
            <Typography level="title-md">W-2 Employee</Typography>
          </Box>
          <Typography level="body-sm" color="neutral">
            Full or part-time employee with taxes withheld
          </Typography>
        </Card>
        <Card
          variant={formData.member_type === '1099' ? 'soft' : 'outlined'}
          color={formData.member_type === '1099' ? 'primary' : 'neutral'}
          sx={{ flex: 1, p: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ReceiptLongOutlined />
            <Typography level="title-md">1099 Contractor</Typography>
          </Box>
          <Typography level="body-sm" color="neutral">
            Independent contractor responsible for own taxes
          </Typography>
        </Card>
      </Box>

      {formData.member_type === '1099' && (
        <Alert color="warning" variant="soft" sx={{ mb: 3 }}>
          As a 1099 contractor, you are responsible for reporting and paying your own taxes.
          Please consult a tax professional if you have questions.
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Job Details */}
      <Typography level="title-md" sx={{ mb: 2 }}>
        Position Details
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <FormControl error={!!errors.job_title}>
          <FormLabel>Job Title *</FormLabel>
          <Input
            value={formData.job_title}
            onChange={(e) => handleChange('job_title', e.target.value)}
            placeholder="e.g., Roofing Technician"
          />
          {errors.job_title && (
            <Typography level="body-xs" color="danger">
              {errors.job_title}
            </Typography>
          )}
        </FormControl>
        <FormControl>
          <FormLabel>Department</FormLabel>
          <Input
            value={formData.department}
            onChange={(e) => handleChange('department', e.target.value)}
            placeholder="e.g., Production"
          />
        </FormControl>
        <FormControl error={!!errors.start_date}>
          <FormLabel>Start Date *</FormLabel>
          <Input
            type="date"
            value={formData.start_date}
            onChange={(e) => handleChange('start_date', e.target.value)}
          />
          {errors.start_date && (
            <Typography level="body-xs" color="danger">
              {errors.start_date}
            </Typography>
          )}
        </FormControl>
      </Box>

      {formData.member_type === 'w2' && (
        <>
          <Divider sx={{ my: 3 }} />

          {/* Pay Information */}
          <Typography level="title-md" sx={{ mb: 2 }}>
            Compensation
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <FormControl>
              <FormLabel>Pay Frequency</FormLabel>
              <RadioGroup
                value={formData.pay_frequency}
                onChange={(e) => handleChange('pay_frequency', e.target.value)}
              >
                <Radio value="hourly" label="Hourly" />
                <Radio value="salary" label="Salary" />
                <Radio value="per_job" label="Per Job" />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>
                {formData.pay_frequency === 'salary' ? 'Annual Salary' : 'Pay Rate'}
              </FormLabel>
              <Input
                startDecorator="$"
                value={formData.pay_rate}
                onChange={(e) => handleChange('pay_rate', e.target.value)}
                placeholder={formData.pay_frequency === 'salary' ? '50000' : '25.00'}
              />
              <Typography level="body-xs" color="neutral">
                This will be confirmed by HR
              </Typography>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Direct Deposit */}
          <Typography level="title-md" sx={{ mb: 2 }}>
            Direct Deposit (Optional)
          </Typography>
          <FormControl sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_direct_deposit ? 'yes' : 'no'}
              onChange={(e) => handleChange('has_direct_deposit', e.target.value === 'yes')}
            >
              <Radio value="yes" label="Yes, set up direct deposit" />
              <Radio value="no" label="No, I'll receive paper checks" />
            </RadioGroup>
          </FormControl>

          {formData.has_direct_deposit && (
            <Box
              sx={{
                bgcolor: 'background.level1',
                borderRadius: 'md',
                p: 2,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <FormControl error={!!errors.bank_name} sx={{ gridColumn: { sm: 'span 2' } }}>
                <FormLabel>Bank Name *</FormLabel>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => handleChange('bank_name', e.target.value)}
                  placeholder="e.g., Chase Bank"
                />
                {errors.bank_name && (
                  <Typography level="body-xs" color="danger">
                    {errors.bank_name}
                  </Typography>
                )}
              </FormControl>
              <FormControl error={!!errors.routing_number}>
                <FormLabel>Routing Number *</FormLabel>
                <Input
                  value={formData.routing_number}
                  onChange={(e) => handleChange('routing_number', e.target.value.replace(/\D/g, '').slice(0, 9))}
                  placeholder="9 digits"
                />
                {errors.routing_number && (
                  <Typography level="body-xs" color="danger">
                    {errors.routing_number}
                  </Typography>
                )}
              </FormControl>
              <FormControl error={!!errors.account_number}>
                <FormLabel>Account Number *</FormLabel>
                <Input
                  value={formData.account_number}
                  onChange={(e) => handleChange('account_number', e.target.value.replace(/\D/g, ''))}
                  placeholder="Account number"
                />
                {errors.account_number && (
                  <Typography level="body-xs" color="danger">
                    {errors.account_number}
                  </Typography>
                )}
              </FormControl>
              <FormControl>
                <FormLabel>Account Type</FormLabel>
                <RadioGroup
                  orientation="horizontal"
                  value={formData.account_type}
                  onChange={(e) => handleChange('account_type', e.target.value)}
                >
                  <Radio value="checking" label="Checking" />
                  <Radio value="savings" label="Savings" />
                </RadioGroup>
              </FormControl>
            </Box>
          )}
        </>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" color="neutral" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleSubmit}>Continue</Button>
      </Box>
    </Box>
  );
};

export default EmploymentTypeStep;
